// Follows "HR Design rules.md": GEN, DIS
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Text field (design system `input`): 32px by default (sm 28px, lg 40px), the `input` token for its border,
 * grey disabled tokens (DIS-03) and the read-only tint (DIS-07: still focusable and copyable).
 */
const inputVariants = cva(
  [
    'flex w-full min-w-0 rounded-md border border-input bg-transparent px-2.5 py-1 text-base shadow-sm transition-[color,border-color,box-shadow] duration-fast ease-standard outline-none md:text-sm',
    'file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground',
    'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:cursor-not-allowed disabled:border-disabled-border disabled:bg-disabled disabled:text-disabled-foreground disabled:shadow-none disabled:placeholder:text-disabled-foreground',
    '[&:read-only:not(:disabled)]:bg-readonly',
    'aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive',
  ],
  {
    variants: {
      size: {
        sm: 'h-7 px-2',
        default: 'h-8',
        lg: 'h-10 px-3',
      },
    },
    defaultVariants: { size: 'default' },
  }
);

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> &
  VariantProps<typeof inputVariants>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, ...props }, ref) => (
    <input
      type={type}
      data-slot="input"
      data-size={size ?? 'default'}
      className={cn(inputVariants({ size }), className)}
      ref={ref}
      {...props}
    />
  )
);
Input.displayName = 'Input';

export { Input, inputVariants };
